import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface Actor {
  id: string;
  name: string;
  image: string;
}

// Featured top models for the squeeze page - hand-picked for appeal
const featuredModels: Actor[] = [
  {
    id: "baddie14",
    name: "Mila",
    image: "/baddies/baddie14.jpg",
  },
  {
    id: "baddie1",
    name: "Aria",
    image: "/baddies/baddie1.jpg",
  },
  {
    id: "baddie10",
    name: "Evelyn",
    image: "/baddies/baddie10.jpg",
  },
  {
    id: "baddie12",
    name: "Emily",
    image: "/baddies/baddie12.jpg",
  },
  {
    id: "baddie25",
    name: "Ruby",
    image: "/baddies/baddie25.jpg",
  },
  {
    id: "baddie21",
    name: "Zoe",
    image: "/baddies/baddie21.jpg",
  },
  {
    id: "baddie3",
    name: "Nova",
    image: "/baddies/baddie3.jpg",
  },
  {
    id: "baddie17",
    name: "Scarlett",
    image: "/baddies/baddie17.jpg",
  },
];

const NoFilter1: React.FC = () => {
  const navigate = useNavigate();

  // Track page view on mount (disabled for showcase)
  useEffect(() => {
    // Analytics removed for showcase site
  }, []);

  const handleCharacterClick = (character: Actor) => {
    // Analytics removed for showcase site

    // Navigate to image-to-video with wan2.2-nofilter model and character image pre-loaded
    navigate("/video/i2v", {
      state: {
        referenceImage: character.image,
        selectedModel: "wan2.2-nofilter",
        noFilterMode: true,
      },
    });
  };

  const handleCreateNowClick = () => {
    // Analytics removed for showcase site

    // Navigate to image-to-video with wan2.2-nofilter model preselected
    navigate("/video/i2v", {
      state: {
        selectedModel: "wan2.2-nofilter",
        noFilterMode: true,
      },
    });
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Hero Section - Above the Fold */}
      <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-20">
        {/* Background Image with overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: "url(/bg.png)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black" />

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto text-center space-y-8">
          {/* Headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight animate-fade-in">
            Turn Anyone Into a <br />
            <span className="text-white">Naughty AI Video 🌶️</span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto animate-slide-up animation-delay-100">
            Choose from 300+ models or upload your own. Create unfiltered content in seconds.
          </p>

          {/* Primary CTA */}
          <button
            onClick={handleCreateNowClick}
            className="group relative inline-flex items-center gap-3 bg-white hover:bg-gray-200 text-black text-xl md:text-2xl font-bold px-12 py-6 rounded-full transition-all duration-300 shadow-[0_0_40px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_rgba(255,255,255,0.5)] hover:scale-110 animate-slide-up animation-delay-200"
          >
            Start Creating Now
            <svg
              className="w-7 h-7 group-hover:translate-x-2 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>

          {/* Social Proof */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-gray-400 text-sm animate-fade-in animation-delay-300">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>No Credit Card Required</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Free Credits to Start</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Results in 60 Seconds</span>
            </div>
          </div>

          {/* Preview Images - Show what's possible */}
          <div className="mt-12 animate-fade-in animation-delay-400">
            <p className="text-sm text-gray-400 mb-4 uppercase tracking-wider">Upload anyone you can image</p>
            <div className="flex items-center justify-center gap-2 md:gap-3 overflow-hidden flex-wrap max-w-4xl mx-auto">
              <img
                src="/baddies/baddie1.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie2.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie3.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie4.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie5.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie6.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie7.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie8.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie9.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie10.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie11.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie12.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie13.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie14.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie15.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie16.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie17.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie18.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie19.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
              <img
                src="/baddies/baddie20.jpg"
                alt="Model preview"
                className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/40 shadow-lg transform hover:scale-110 transition-transform hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Featured Models Section */}
      <div className="relative px-4 py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto space-y-12">
          {/* Section Header */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/20 rounded-full px-4 py-2 mb-4">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span className="text-white text-sm font-semibold">300+ Models Available</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white">
              Pick Your Perfect Model 🔥
            </h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              One click and you're ready to create. No signup required to browse.
            </p>
          </div>

          {/* Models Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {featuredModels.map((model, index) => (
              <button
                key={model.id}
                onClick={() => handleCharacterClick(model)}
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden border-2 border-white/20 hover:border-white/60 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <img
                  src={model.image}
                  alt={model.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300" />
                
                {/* Model Name */}
                <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-0 transition-transform duration-300">
                  <h3 className="text-white font-bold text-lg md:text-xl drop-shadow-lg">
                    {model.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-white text-sm font-semibold">Click to Create</span>
                    <svg className="w-4 h-4 text-white group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Or Upload Your Own */}
          <div className="text-center space-y-4">
            <p className="text-gray-400">Or upload your own image</p>
            <button
              onClick={handleCreateNowClick}
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/40 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Upload Your Own Image
            </button>
          </div>
        </div>
      </div>

      {/* Example Videos Section */}
      <div className="relative px-4 py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto space-y-12">
          {/* Section Header */}
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-5xl font-bold text-white">
              See What's Possible
            </h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              Real videos created with our platform in under 60 seconds
            </p>
          </div>

          {/* Videos Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <div className="group relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/final_8s.mp4"
                poster="/baddies/baddie14.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white font-semibold text-sm drop-shadow-lg">
                  Mila's Secret
                </h3>
              </div>
            </div>

            <div className="group relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/example_2.mp4"
                poster="/baddies/baddie1.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white font-semibold text-sm drop-shadow-lg">
                  Aria's Dressover
                </h3>
              </div>
            </div>

            <div className="group relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/example_3.mp4"
                poster="/baddies/baddie10.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white font-semibold text-sm drop-shadow-lg">
                  Evelyn's Happy Place
                </h3>
              </div>
            </div>

            <div className="group relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/example_4.mp4"
                poster="/baddies/baddie12.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white font-semibold text-sm drop-shadow-lg">
                  Emily Wilding Out
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="relative px-4 py-32 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-6xl font-black leading-tight text-white">
            Ready to Create Your First Video?
          </h2>
          <p className="text-xl md:text-2xl text-gray-300">
            Join thousands of creators making viral content with AI
          </p>
          <button
            onClick={handleCreateNowClick}
            className="group relative inline-flex items-center gap-3 bg-white hover:bg-gray-200 text-black text-xl md:text-2xl font-bold px-12 py-6 rounded-full transition-all duration-300 shadow-[0_0_40px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_rgba(255,255,255,0.5)] hover:scale-110"
          >
            Get Started Free
            <svg
              className="w-7 h-7 group-hover:translate-x-2 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Disclaimer Footer */}
      <div className="relative px-4 py-8 bg-black border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm leading-relaxed">
            The "No Filter" mode is designed for creative and entertainment purposes. 
            Users are responsible for ensuring their content complies with platform 
            guidelines and applicable laws. PixelMaker does not endorse or take 
            responsibility for user-generated content.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NoFilter1;

